import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Shield, Lock, User } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function MasterAdminLogin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [setupMode, setSetupMode] = useState(false);
  const [setupData, setSetupData] = useState({ username: '', email: '', password: '', name: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/master-admin/login`, formData);
      localStorage.setItem('masterAdminToken', response.data.access_token);
      localStorage.setItem('masterAdmin', JSON.stringify(response.data.admin));
      toast.success('Login successful');
      navigate('/master-admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/master-admin/setup`, setupData);
      toast.success('Master Admin created successfully. Please login.');
      setSetupMode(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Setup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 bg-slate-800/50 border-slate-700 backdrop-blur-sm">
        <div className="text-center mb-8">
          <div className="inline-flex p-3 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Master Admin Portal</h1>
          <p className="text-slate-400 text-sm mt-2">Solution Provider Access</p>
        </div>

        {setupMode ? (
          <form onSubmit={handleSetup} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Full Name</label>
              <Input
                value={setupData.name}
                onChange={(e) => setSetupData({ ...setupData, name: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="Admin Name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Username</label>
              <Input
                value={setupData.username}
                onChange={(e) => setSetupData({ ...setupData, username: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="admin"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
              <Input
                type="email"
                value={setupData.email}
                onChange={(e) => setSetupData({ ...setupData, email: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="admin@company.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
              <Input
                type="password"
                value={setupData.password}
                onChange={(e) => setSetupData({ ...setupData, password: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="••••••••"
                required
              />
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600" disabled={loading}>
              {loading ? 'Creating...' : 'Create Master Admin'}
            </Button>
            <Button type="button" variant="ghost" className="w-full text-slate-400" onClick={() => setSetupMode(false)}>
              Back to Login
            </Button>
          </form>
        ) : (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Username</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="master-admin-username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white pl-10"
                  placeholder="Enter username"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="master-admin-password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white pl-10"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>
            <Button 
              type="submit" 
              data-testid="master-admin-login-btn"
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600" 
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            <div className="text-center">
              <button
                type="button"
                className="text-sm text-slate-400 hover:text-amber-400 transition-colors"
                onClick={() => setSetupMode(true)}
              >
                First time? Setup Master Admin
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 pt-6 border-t border-slate-700 text-center">
          <a href="/login" className="text-sm text-slate-400 hover:text-white transition-colors">
            ← Back to Customer Login
          </a>
        </div>
      </Card>
    </div>
  );
}

export default MasterAdminLogin;
